// Documents juridiques. Rédigés à partir du fonctionnement réel du service.
// `draft: true` affiche un bandeau « à faire valider par un juriste » : aucun
// document n'est présenté comme définitif tant qu'il n'a pas été relu.

export const COMPANY = {
  name: "Perform",
  legalName: "[Raison sociale à compléter]",
  form: "[Forme juridique à compléter]",
  capital: "[Capital social à compléter]",
  siren: "[SIREN à compléter]",
  vat: "[N° TVA intracommunautaire à compléter]",
  address: "[Adresse du siège à compléter]",
  director: "[Directeur de la publication à compléter]",
  host: "Railway Corp., 2261 Market Street #4667, San Francisco, CA 94114, États-Unis",
  emails: {
    general: "bonjour@performcustom.com",
    support: "support@performcustom.com",
    privacy: "confidentialite@performcustom.com",
    security: "securite@performcustom.com",
    legal: "legal@performcustom.com",
    press: "presse@performcustom.com",
    partners: "partenaires@performcustom.com",
  },
};

const UPDATED = "16 septembre 2026";

export const LEGAL_DOCS = [
  {
    slug: "cgu", category: "Contrat", draft: true,
    title: "Conditions générales d'utilisation",
    short: "Le contrat entre vous et Perform.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "objet", heading: "1. Objet", blocks: [
        { t: "p", v: "Perform est une plateforme de coaching esport : un coach unique propose des séances aux joueurs qui souhaitent progresser. Les présentes conditions régissent l'accès au service et son utilisation. En créant un compte, vous les acceptez." },
        { t: "p", v: "Les séances de coaching sont fournies par le coach, qui en est seul responsable de l'exécution." },
      ]},
      { id: "compte", heading: "2. Compte", blocks: [
        { t: "p", v: "L'accès nécessite un compte, créé par lien de connexion envoyé à votre adresse e-mail. Il n'y a pas de mot de passe : la sécurité de votre compte dépend donc de celle de votre boîte mail." },
        { t: "ul", v: [
          "Vous devez avoir 15 ans révolus. Entre 15 et 18 ans, l'accord d'un représentant légal est requis.",
          "Vous fournissez des informations exactes et les tenez à jour.",
          "Un compte est personnel. Vous ne le partagez pas.",
          "Vous nous signalez sans délai tout usage non autorisé à " + COMPANY.emails.security + ".",
        ]},
      ]},
      { id: "coachs", heading: "3. Coachs", blocks: [
        { t: "p", v: "Un coach publie un profil, des créneaux et des tarifs qu'il fixe librement. Il s'engage à disposer des compétences annoncées, à honorer les séances réservées et à respecter la charte d'usage acceptable." },
        { t: "p", v: "Perform vérifie l'identité et le niveau déclaré avant publication. Cette vérification ne constitue ni un agrément, ni une garantie de résultat." },
      ]},
      { id: "seances", heading: "4. Séances et paiements", blocks: [
        { t: "p", v: "Une réservation forme un contrat entre le joueur et le coach. Perform en assure la facilitation technique et perçoit une commission décrite sur la page [Tarifs](/tarifs)." },
        { t: "note", kind: "info", v: "Les paiements en ligne ne sont pas encore activés. Cette section décrit le fonctionnement prévu et entrera en vigueur à l'ouverture des paiements, après mise à jour de ce document." },
      ]},
      { id: "contenu", heading: "5. Contenus", blocks: [
        { t: "p", v: "Vous conservez la propriété des contenus que vous déposez (VOD, messages, profil). Vous accordez à Perform une licence non exclusive et gratuite, limitée à l'hébergement et à l'affichage nécessaires au fonctionnement du service, pour la durée de votre compte." },
        { t: "p", v: "Vous garantissez détenir les droits sur ce que vous déposez." },
      ]},
      { id: "resiliation", heading: "6. Durée et résiliation", blocks: [
        { t: "p", v: "Le contrat est à durée indéterminée. Vous pouvez supprimer votre compte à tout moment depuis vos paramètres ou en écrivant à " + COMPANY.emails.support + "." },
        { t: "p", v: "Perform peut suspendre un compte en cas de manquement grave aux présentes conditions ou à la charte d'usage acceptable, après information de l'utilisateur sauf urgence ou obligation légale." },
      ]},
      { id: "responsabilite", heading: "7. Responsabilité", blocks: [
        { t: "p", v: "Perform fournit le service avec diligence mais ne garantit ni disponibilité ininterrompue, ni résultat de progression. La responsabilité de Perform ne saurait être engagée au-delà de la prestation effectivement commandée." },
        { t: "p", v: "Aucune clause n'a pour effet de limiter les droits que la loi française reconnaît aux consommateurs." },
      ]},
      { id: "droit", heading: "8. Droit applicable", blocks: [
        { t: "p", v: "Droit français. En cas de litige, une solution amiable sera recherchée en priorité. Le consommateur peut recourir gratuitement à un médiateur de la consommation." },
      ]},
    ],
  },
  {
    slug: "confidentialite", category: "Données", draft: true,
    title: "Politique de confidentialité",
    short: "Ce que nous collectons, pourquoi, et vos droits.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "responsable", heading: "1. Responsable du traitement", blocks: [
        { t: "p", v: `${COMPANY.legalName}, ${COMPANY.address}. Contact : ${COMPANY.emails.privacy}.` },
      ]},
      { id: "donnees", heading: "2. Données traitées", blocks: [
        { t: "table", head: ["Donnée", "Finalité", "Base légale", "Conservation"], rows: [
          ["Adresse e-mail", "Connexion, notifications de service", "Exécution du contrat", "Durée du compte"],
          ["Pseudo, prénom, nom", "Affichage du profil", "Exécution du contrat", "Durée du compte"],
          ["Photo de profil", "Affichage du profil", "Consentement", "Jusqu'au retrait"],
          ["Jeu, palier, objectif", "Recommandation de coachs", "Exécution du contrat", "Durée du compte"],
          ["Sessions (empreinte du jeton, agent utilisateur)", "Maintien de la connexion, détection d'anomalie", "Intérêt légitime — sécurité", "30 jours"],
          ["Liens de connexion", "Authentification sans mot de passe", "Exécution du contrat", "15 minutes"],
          ["Journaux serveur", "Sécurité, diagnostic", "Intérêt légitime", "30 jours"],
        ]},
        { t: "p", v: "Nous ne collectons aucune donnée bancaire : les paiements ne sont pas activés. Aucun traceur publicitaire tiers n'est déposé." },
      ]},
      { id: "transferts", heading: "3. Hébergement et transferts", blocks: [
        { t: "p", v: "Le service est hébergé chez Railway et les e-mails sont acheminés par Resend, deux prestataires établis aux États-Unis. Vos données sont donc transférées hors de l'Union européenne." },
        { t: "note", v: "Ces transferts doivent être encadrés par des clauses contractuelles types. La formalisation avec chaque prestataire est en cours : la liste à jour figure sur la page [Sous-traitants](/legal/sous-traitants)." },
      ]},
      { id: "droits", heading: "4. Vos droits", blocks: [
        { t: "p", v: "Vous disposez des droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité. Écrivez à " + COMPANY.emails.privacy + " : nous répondons sous un mois." },
        { t: "p", v: "Vous pouvez introduire une réclamation auprès de la CNIL (cnil.fr)." },
      ]},
      { id: "securite", heading: "5. Sécurité", blocks: [
        { t: "p", v: "Chiffrement en transit (TLS 1.3), cookie de session HttpOnly, jetons de session stockés sous forme d'empreinte irréversible, absence de mot de passe. Le détail figure sur la page [Sécurité](/securite)." },
      ]},
    ],
  },
  {
    slug: "cookies", category: "Données", draft: true,
    title: "Politique de cookies",
    short: "Trois cookies, aucun traceur publicitaire.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "liste", heading: "1. Ce que nous déposons", blocks: [
        { t: "table", head: ["Nom", "Rôle", "Catégorie", "Durée"], rows: [
          ["`perform_session`", "Maintenir votre connexion", "Strictement nécessaire", "30 jours"],
          ["`perform_cookie_prefs`", "Mémoriser votre choix de cookies", "Strictement nécessaire", "6 mois"],
          ["`perform_gs_open`", "État replié de la liste de prise en main", "Préférences", "Persistant"],
        ]},
        { t: "p", v: "Les cookies strictement nécessaires ne requièrent pas de consentement. Nous n'utilisons aujourd'hui **aucun cookie analytique ni publicitaire**." },
      ]},
      { id: "gestion", heading: "2. Gérer vos choix", blocks: [
        { t: "p", v: "Le bandeau affiché à votre première visite vous permet d'accepter ou de refuser les catégories non essentielles. Vous pouvez revenir sur votre choix à tout moment via « Préférences cookies » en bas de page." },
      ]},
    ],
  },
  {
    slug: "usage-acceptable", category: "Règles", draft: true,
    title: "Charte d'usage acceptable",
    short: "Ce qui n'a pas sa place sur Perform.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "interdit", heading: "1. Interdictions", blocks: [
        { t: "ul", v: [
          "Harcèlement, propos haineux, discriminatoires ou menaces.",
          "Usurpation d'identité ou fausse déclaration de niveau de jeu.",
          "Vente de comptes, boosting sur compte tiers, contournement des règles d'un éditeur de jeu.",
          "Diffusion de logiciels de triche ou incitation à leur usage.",
          "Contenus à caractère sexuel impliquant des mineurs — signalement immédiat aux autorités.",
          "Extraction automatisée massive du service ou contournement des limites de débit.",
        ]},
      ]},
      { id: "signalement", heading: "2. Signaler", blocks: [
        { t: "p", v: `Écrivez à ${COMPANY.emails.support}. Les signalements concernant des mineurs ou des menaces graves sont traités en priorité.` },
      ]},
      { id: "sanctions", heading: "3. Sanctions", blocks: [
        { t: "p", v: "Selon la gravité : avertissement, retrait du contenu, suspension temporaire ou fermeture définitive du compte. Les manquements pénalement répréhensibles sont signalés aux autorités compétentes." },
      ]},
    ],
  },
  {
    slug: "dpa", category: "Données", draft: true,
    title: "Accord de traitement des données",
    short: "Pour les clubs et structures qui nous confient des données.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "portee", heading: "1. Portée", blocks: [
        { t: "p", v: "Cet accord s'applique lorsqu'une structure (club, équipe, école) utilise Perform pour gérer les comptes de ses membres. La structure est alors responsable de traitement ; Perform agit comme sous-traitant au sens de l'article 28 du RGPD." },
        { t: "note", v: "Pour un joueur ou un coach individuel, c'est la [politique de confidentialité](/legal/confidentialite) qui s'applique, et non ce document." },
      ]},
      { id: "engagements", heading: "2. Engagements de Perform", blocks: [
        { t: "ul", v: [
          "Traiter les données uniquement sur instruction documentée du responsable.",
          "Garantir la confidentialité des personnes autorisées à traiter les données.",
          "Mettre en œuvre les mesures de sécurité décrites sur la page [Sécurité](/securite).",
          "N'engager de sous-traitant ultérieur qu'après information, avec droit d'objection.",
          "Assister le responsable pour les demandes d'exercice de droits et les violations de données.",
          "Supprimer ou restituer les données à la fin de la prestation.",
        ]},
      ]},
      { id: "notification", heading: "3. Violation de données", blocks: [
        { t: "p", v: "Perform notifie le responsable de traitement sans délai injustifié après avoir pris connaissance d'une violation, et au plus tard sous 48 heures, avec les éléments connus à ce stade." },
      ]},
      { id: "signature", heading: "4. Signature", blocks: [
        { t: "p", v: `Pour recevoir un exemplaire signé, écrivez à ${COMPANY.emails.legal}.` },
      ]},
    ],
  },
  {
    slug: "conditions-ia", category: "Règles", draft: true,
    title: "Conditions relatives à l'IA",
    short: "Aucune donnée n'est aujourd'hui envoyée à un modèle.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "etat", heading: "1. État actuel", blocks: [
        { t: "note", kind: "info", v: "Les fonctionnalités d'intelligence artificielle sont en cours de développement et **ne sont pas activées**. À ce jour, aucune donnée utilisateur n'est transmise à un fournisseur de modèle." },
        { t: "p", v: "Ce document décrit les règles qui s'appliqueront à leur activation. Il sera mis à jour, et les utilisateurs informés, avant toute mise en service." },
      ]},
      { id: "principes", heading: "2. Principes engagés", blocks: [
        { t: "ul", v: [
          "Vos données ne serviront jamais à entraîner des modèles, ni les nôtres ni ceux de tiers.",
          "Chaque fonctionnalité IA indiquera clairement quelles données elle transmet.",
          "Un retrait sera possible sans perdre l'accès au reste du service.",
          "Les fournisseurs de modèles figureront sur la page [Sous-traitants](/legal/sous-traitants).",
          "Une suggestion produite par un modèle reste une suggestion : elle n'engage pas de décision automatisée à effet juridique.",
        ]},
        { t: "p", v: "Le détail figure sur la page [Transparence IA](/ia)." },
      ]},
    ],
  },
  {
    slug: "conditions-developpeurs", category: "Règles", draft: true,
    title: "Conditions développeurs",
    short: "Règles d'usage de l'API.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "acces", heading: "1. Accès", blocks: [
        { t: "p", v: "L'API est accessible avec une session utilisateur valide. Vous n'accédez qu'aux données du compte connecté." },
      ]},
      { id: "regles", heading: "2. Règles", blocks: [
        { t: "ul", v: [
          "Respectez les limites de débit publiées et l'en-tête `Retry-After`.",
          "N'utilisez pas l'API pour reconstituer une base de coachs ou d'utilisateurs.",
          "Ne stockez pas les jetons de session en clair dans une application cliente.",
          "Signalez toute faille via la page [Divulgation de vulnérabilité](/securite/divulgation).",
        ]},
      ]},
      { id: "evolution", heading: "3. Évolutions", blocks: [
        { t: "p", v: "L'API est en version initiale et peut évoluer. Les changements incompatibles seront annoncés sur la page [Nouveautés](/nouveautes) au moins 30 jours à l'avance." },
      ]},
    ],
  },
  {
    slug: "annulation", category: "Contrat", draft: true,
    title: "Annulation et remboursement",
    short: "Annulation gratuite jusqu'à 24 heures avant.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "principe", heading: "1. Principe", blocks: [
        { t: "table", head: ["Situation", "Conséquence"], rows: [
          ["Annulation par le joueur, plus de 24 h avant", "Remboursement intégral"],
          ["Annulation par le joueur, moins de 24 h avant", "Séance due au coach"],
          ["Annulation par le coach", "Remboursement intégral, quel que soit le délai"],
          ["Coach absent sans prévenir", "Remboursement intégral et signalement"],
          ["Problème technique imputable à Perform", "Report ou remboursement, au choix du joueur"],
        ]},
      ]},
      { id: "retractation", heading: "2. Droit de rétractation", blocks: [
        { t: "p", v: "Pour une prestation de service à date convenue, le droit de rétractation de 14 jours ne s'applique pas dès lors que la séance est fixée (article L221-28 du Code de la consommation). Notre politique d'annulation ci-dessus reste applicable." },
      ]},
      { id: "activation", heading: "3. Activation", blocks: [
        { t: "note", kind: "info", v: "Les paiements n'étant pas encore activés, aucun remboursement n'est à ce jour possible ou nécessaire. Cette politique entrera en vigueur avec l'ouverture des paiements." },
      ]},
    ],
  },
  {
    slug: "mentions", category: "Informations", draft: false,
    title: "Mentions légales",
    short: "Éditeur, hébergeur, contacts.",
    version: "1.0", effective: UPDATED, updated: UPDATED,
    sections: [
      { id: "editeur", heading: "Éditeur", blocks: [
        { t: "table", head: ["", ""], rows: [
          ["Raison sociale", COMPANY.legalName],
          ["Forme juridique", COMPANY.form],
          ["Capital social", COMPANY.capital],
          ["SIREN", COMPANY.siren],
          ["TVA intracommunautaire", COMPANY.vat],
          ["Siège social", COMPANY.address],
          ["Directeur de la publication", COMPANY.director],
          ["Contact", COMPANY.emails.general],
        ]},
        { t: "note", v: "Les mentions entre crochets doivent être complétées avec les informations officielles de la société avant toute exploitation commerciale du site. Elles sont obligatoires (article 6 de la LCEN)." },
      ]},
      { id: "hebergeur", heading: "Hébergeur", blocks: [
        { t: "p", v: COMPANY.host },
        { t: "p", v: "Nom de domaine et service DNS : Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, États-Unis." },
      ]},
      { id: "propriete", heading: "Propriété intellectuelle", blocks: [
        { t: "p", v: "La marque Perform, le logo et l'interface sont protégés. Les noms de jeux et marques tierces cités appartiennent à leurs éditeurs respectifs ; leur mention ne vaut ni partenariat ni approbation." },
      ]},
    ],
  },
];

export const findLegal = slug => LEGAL_DOCS.find(d => d.slug === slug);
